import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { DocumentStatus, Document, Folder } from '@/gql/graphql';
import { FileStatus, FolderTreeItem } from '@/types/file';
import { CustomArchiveBuilder } from '@/utils/customArchive';
import { createFolderTree } from '@/utils/folder_utils';
import { generate_uuid } from '@/utils/uuid_generator';
import { useAdminSocket } from './AdminSocketContext';
import { useKeycloakAuth } from './KeycloakAuthProvider';

export interface UploadProgress {
  progress: number;
  filesProcessed: number;
  totalFiles: number;
  filesFailed: number;
  phase: 'preparing' | 'uploading' | 'processing' | 'complete';
}

interface IUploadContext {
  uploadFolderTree: (rootFolder: FolderTreeItem) => Promise<void>;
  uploadFolderArchive: (files: File[], folderId: string, selectedFolder: Folder) => Promise<void>;
  uploadProgress: Record<string, UploadProgress>;
  fileStatuses: Record<string, FileStatus>;
  setFileStatus: (fileId: string, status: Partial<FileStatus>) => void;
  clearFileStatuses: () => void;
}

const UploadContext = createContext<IUploadContext>({
  uploadFolderTree: async () => {},
  uploadFolderArchive: async () => {},
  uploadProgress: {},
  fileStatuses: {},
  setFileStatus: () => {},
  clearFileStatuses: () => {}
});

export const useUpload = () => useContext(UploadContext);

type EnrichedDocument = Document & { folderName: string; parentFolderId: string | null };

const collectDocuments = (folder: FolderTreeItem, depth = 0, path = '', maxDepth = 10): EnrichedDocument[] => {
  const folderName = folder.name || 'unnamed';
  const currentPath = path ? `${path}/${folderName}` : folderName;

  if (depth === 0) {
    console.log(`[collectDocuments] Starting document collection with root folder: ${folderName}`);
  }

  if (depth > maxDepth) {
    console.warn(`[collectDocuments] Max folder depth (${maxDepth}) reached for path: ${currentPath}. Some files may be skipped.`);
    return [];
  }

  let docs: EnrichedDocument[] = (folder.documents || []).map((doc) => ({
    ...doc,
    folderName: folder.name,
    parentFolderId: (folder as FolderTreeItem & { parentId?: string }).parentId || null
  }));

  if (folder.subFolders && folder.subFolders.length > 0) {
    folder.subFolders.forEach((subFolder) => {
      const subDocs = collectDocuments(subFolder, depth + 1, currentPath, maxDepth);
      docs = docs.concat(subDocs);
    });
  }

  if (depth === 0) {
    console.log(`[collectDocuments] Total documents collected: ${docs.length}`);
  }

  return docs;
};

export const UploadProvider = ({ children }: { children: ReactNode }) => {
  const { socket } = useAdminSocket();
  const { refreshAccessToken } = useKeycloakAuth();
  const [fileStatuses, setFileStatuses] = useState<Record<string, FileStatus>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({});

  useEffect(() => {
    if (!socket) return;

    const handleDocumentStatusReport = (data: { fileID: string; status: string; progress: number; message: string }) => {
      const progress = Math.round(Math.min(100, Math.max(0, data.progress)) / 5) * 5;
      setFileStatuses((prev) => {
        const currentStatus = prev[data.fileID];
        if (!currentStatus) return prev;
        return {
          ...prev,
          [data.fileID]: { ...currentStatus, progress, isUploading: progress < 100 }
        };
      });
    };

    const handleDocumentProcessed = (data: { fileID: string; status: 'success' | 'error' }) => {
      if (data.status === 'success') {
        setFileStatuses((prev) => {
          const newStatuses = { ...prev };
          delete newStatuses[data.fileID];
          if (Object.keys(newStatuses).length === 0) return {};
          return newStatuses;
        });
      }
    };

    const handleDocumentError = (error: { fileID: string; message: string }) => {
      console.error('[Upload] Document error:', error);
      setFileStatuses((prev) => {
        const newStatuses = { ...prev };
        delete newStatuses[error.fileID];
        return newStatuses;
      });
    };

    const handleFolderUploadProgress = (data: {
      uploadId: string;
      progress: number;
      filesProcessed: number;
      totalFiles: number;
      filesFailed: number;
    }) => {
      setUploadProgress((prev) => ({
        ...prev,
        [data.uploadId]: {
          progress: data.progress,
          filesProcessed: data.filesProcessed,
          totalFiles: data.totalFiles,
          filesFailed: data.filesFailed,
          phase: data.progress >= 100 ? 'complete' : 'processing'
        }
      }));
    };

    socket.on('document_status_report', handleDocumentStatusReport);
    socket.on('document_processed', handleDocumentProcessed);
    socket.on('document_error', handleDocumentError);
    socket.on('folder_created', (data: { path: string }) => console.log('[Upload] Folder created:', data.path));
    socket.on('folder_error', (error: { path: string; message: string }) => console.error('[Upload] Folder error:', error));
    socket.on('folder_upload_progress', handleFolderUploadProgress);

    return () => {
      socket.off('document_status_report', handleDocumentStatusReport);
      socket.off('document_processed', handleDocumentProcessed);
      socket.off('document_error', handleDocumentError);
      socket.off('folder_created');
      socket.off('folder_error');
      socket.off('folder_upload_progress', handleFolderUploadProgress);
    };
  }, [socket]);

  const setFileStatus = useCallback((fileId: string, status: Partial<FileStatus>) => {
    setFileStatuses((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        ...status,
        progress: status.progress ?? prev[fileId]?.progress ?? 0,
        isUploading: status.isUploading ?? prev[fileId]?.isUploading ?? true,
        documentId: status.documentId ?? prev[fileId]?.documentId ?? fileId,
        folderId: status.folderId ?? prev[fileId]?.folderId ?? ''
      }
    }));
  }, []);

  const clearFileStatuses = useCallback(() => setFileStatuses({}), []);

  const uploadFolderTree = useCallback(
    async (folderTree: FolderTreeItem) => {
      try {
        await refreshAccessToken();
      } catch {
        // Proceed; socket will fail if not connected
      }

      try {
        const metadataTree = createFolderTree(folderTree);
        const response = await socket?.emitWithAck('document_create_folder_structure', { folder_structure: metadataTree });

        if (!response?.success) {
          throw new Error('Failed to create folder structure');
        }

        if (response.root_folder_id) {
          folderTree.id = response.root_folder_id;
        }

        clearFileStatuses();

        const documents = collectDocuments(folderTree);

        for (const doc of documents) {
          const fileData = doc.text || '';
          const chunkSize = 4 * 1024;
          const chunkCount = Math.max(1, Math.ceil(fileData.length / chunkSize));

          setFileStatus(doc.id, { progress: 0, isUploading: true, documentId: doc.id, folderId: doc.folderId });

          const initPayload = {
            fileID: doc.id,
            file_config: {
              fileID: doc.id,
              title: doc.title,
              extension: doc.extension,
              content: '',
              folder_id: doc.folderId,
              folder_name: (doc as EnrichedDocument).folderName || undefined,
              parent_folder_id: (doc as EnrichedDocument).parentFolderId || undefined,
              file_size: doc.fileSize,
              status: DocumentStatus.Pending,
              user_id: doc.userId,
              isURL: false,
              overwrite: false,
              source: 'upload'
            },
            total: chunkCount
          };

          const initResponse = await socket?.emitWithAck('document_batch', initPayload);
          if (!initResponse?.success) {
            throw new Error(`Failed to start upload for document: ${doc.title}`);
          }

          for (let i = 0; i < chunkCount; i++) {
            const chunk = fileData.slice(i * chunkSize, (i + 1) * chunkSize);
            const chunkResponse = await socket?.emitWithAck('document_batch', {
              fileID: doc.id,
              chunk,
              order: i,
              total: chunkCount,
              isLastChunk: i === chunkCount - 1
            });

            if (!chunkResponse?.success) {
              throw new Error(`Failed to upload chunk ${i + 1} for document: ${doc.title}`);
            }

            setFileStatus(doc.id, { progress: Math.round(((i + 1) / chunkCount) * 100) });
          }
        }

        const completeResponse = await socket?.emitWithAck('document_upload_complete', {});
        if (!completeResponse?.success) {
          const serverMessage =
            (completeResponse && (completeResponse.message || completeResponse.error || completeResponse.code)) || 'Unknown server error';
          throw new Error(`Failed to complete document batch upload: ${serverMessage}`);
        }

        clearFileStatuses();
      } catch (error) {
        console.error('[Upload] Error uploading folder tree:', error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- socket is the meaningful dep
    [socket]
  );

  const uploadFolderArchive = useCallback(
    async (files: File[], folderId: string, selectedFolder: Folder) => {
      try {
        await refreshAccessToken();
      } catch {
        // Socket will fail if not connected
      }

      const uploadId = generate_uuid();

      setUploadProgress((prev) => ({
        ...prev,
        [uploadId]: { progress: 0, filesProcessed: 0, totalFiles: files.length, filesFailed: 0, phase: 'preparing' }
      }));

      const archiveBuilder = new CustomArchiveBuilder();
      const archiveBlob = await archiveBuilder.buildFromFiles(files, (progress, currentFile) => {
        setUploadProgress((prev) => ({
          ...prev,
          [uploadId]: { ...prev[uploadId], progress, filesProcessed: currentFile, phase: 'preparing' }
        }));
      });

      setUploadProgress((prev) => ({
        ...prev,
        [uploadId]: { ...prev[uploadId], progress: 0, phase: 'uploading' }
      }));

      const CHUNK_SIZE = 256 * 1024;
      const totalSize = archiveBlob.size;
      const chunkCount = Math.ceil(totalSize / CHUNK_SIZE);

      const initResponse = await socket?.emitWithAck('folder_upload_init', {
        uploadId,
        folderId,
        folderName: selectedFolder.name,
        fileCount: files.length,
        totalSize,
        chunkCount,
        format: 'custom',
        metadata: { timestamp: new Date().toISOString(), source: 'web_upload' }
      });

      if (!initResponse?.success) {
        throw new Error('Failed to initialize folder upload');
      }

      let lastProgressUpdate = Date.now();
      let lastProgress = 0;
      const PROGRESS_UPDATE_INTERVAL = 500;
      const PROGRESS_UPDATE_THRESHOLD = 5;

      for (let i = 0; i < chunkCount; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, totalSize);
        const chunkData = await archiveBlob.slice(start, end).arrayBuffer();

        const chunkResponse = await socket?.emitWithAck('folder_upload_chunk', {
          uploadId,
          chunkIndex: i,
          chunkData,
          isLastChunk: i === chunkCount - 1
        });

        if (!chunkResponse?.success) {
          throw new Error(`Failed to upload chunk ${i + 1}/${chunkCount}`);
        }

        const progress = ((i + 1) / chunkCount) * 100;
        const now = Date.now();
        const isLastChunk = i === chunkCount - 1;

        if (isLastChunk || now - lastProgressUpdate >= PROGRESS_UPDATE_INTERVAL || Math.abs(progress - lastProgress) >= PROGRESS_UPDATE_THRESHOLD) {
          setUploadProgress((prev) => ({ ...prev, [uploadId]: { ...prev[uploadId], progress, phase: 'uploading' } }));
          lastProgressUpdate = now;
          lastProgress = progress;
        }
      }

      setUploadProgress((prev) => ({
        ...prev,
        [uploadId]: { ...prev[uploadId], progress: 0, filesProcessed: 0, phase: 'processing' }
      }));

      const completeResponse = await socket?.emitWithAck('folder_upload_complete', { uploadId, folderId });

      if (!completeResponse?.success) {
        throw new Error('Failed to complete folder upload');
      }

      setUploadProgress((prev) => ({
        ...prev,
        [uploadId]: {
          ...prev[uploadId],
          progress: 100,
          phase: 'complete',
          filesProcessed: completeResponse.filesProcessed || prev[uploadId]?.filesProcessed || 0,
          filesFailed: completeResponse.filesFailed || prev[uploadId]?.filesFailed || 0
        }
      }));

      setTimeout(() => {
        setUploadProgress((prev) => {
          const { [uploadId]: _, ...rest } = prev;
          return rest;
        });
      }, 3000);
    },
    [socket, refreshAccessToken]
  );

  const contextValue = useMemo(
    () => ({ uploadFolderTree, uploadFolderArchive, uploadProgress, fileStatuses, setFileStatus, clearFileStatuses }),
    [uploadFolderTree, uploadFolderArchive, uploadProgress, fileStatuses, setFileStatus, clearFileStatuses]
  );

  return (
    <UploadContext.Provider value={contextValue}>
      {children}
    </UploadContext.Provider>
  );
};
